import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI, devopsAPI, systemSettingsAPI } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import AttendanceStatsWidget from '../components/AttendanceStatsWidget';
import { CRMPipelineChart, TimesheetTrendChart, ExpenseBreakdownChart, DepartmentChart, HeadcountChart } from '../components/dashboard/DashboardCharts';
import {
  Users, CalendarDays, Receipt, Package, UserPlus, TrendingUp,
  Clock, CheckCircle, ChevronRight, CalendarCheck, PartyPopper,
  FileWarning, AlertTriangle, Download, BarChart3, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const StatCard = ({ title, value, icon: Icon, description, color = 'primary', onClick, href }) => {
  const navigate = useNavigate();
  const handleClick = () => { if (onClick) onClick(); else if (href) navigate(href); };
  const isClickable = onClick || href;
  return (
    <Card
      className={`hover:shadow-md transition-all duration-200 ${isClickable ? 'cursor-pointer hover:border-primary/50 hover:scale-[1.02]' : ''}`}
      onClick={isClickable ? handleClick : undefined}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold mt-2 font-['Manrope']">{value}</p>
            {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className={`p-3 rounded-xl bg-${color}/10`}>
              <Icon className={`w-6 h-6 text-${color}`} />
            </div>
            {isClickable && <ChevronRight className="w-4 h-4 text-muted-foreground" />}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function DashboardPage() {
  const { user, canApprove, isAdmin, isHR, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [workItems, setWorkItems] = useState([]);
  const [expiryData, setExpiryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const isHRAdmin = isAdmin || isHR || isSuperAdmin;

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Resilient: each call is independent so one failure (e.g. DevOps not configured)
        // can never blank the whole Hub. Stats always render if they load.
        const [statsRes, projectsRes, expiryRes] = await Promise.all([
          dashboardAPI.getStats().catch((e) => { console.error('Dashboard stats failed:', e); return { data: null }; }),
          devopsAPI.getProjects().catch((e) => { console.error('DevOps projects failed:', e); return { data: [] }; }),
          isHRAdmin
            ? systemSettingsAPI.getDocumentExpirySummary().catch(() => ({ data: null }))
            : Promise.resolve({ data: null }),
        ]);
        setStats(statsRes.data);
        if (expiryRes?.data) setExpiryData(expiryRes.data);
        if (projectsRes.data?.length > 0) {
          try {
            const workItemsRes = await devopsAPI.getWorkItems(projectsRes.data[0].name, 5);
            setWorkItems(workItemsRes.data);
          } catch (e) {
            console.error('DevOps work items failed:', e);
          }
        }
      } catch (error) {
        console.error('Dashboard fetch error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isHRAdmin]);

  const loadAnalytics = async () => {
    if (analytics) return;
    setAnalyticsLoading(true);
    try {
      const res = await dashboardAPI.getAnalytics();
      setAnalytics(res.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const downloadReport = async (period) => {
    setDownloading(true);
    try {
      const res = await dashboardAPI.downloadReport(period);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `dashboard_report_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="dashboard-loading" className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-8 animate-fadeIn">
      {/* Welcome Section */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary to-violet-500 p-8 text-white">
        <div className="relative z-10">
          <p className="text-sm font-medium opacity-90">Welcome back,</p>
          <h1 className="text-3xl font-bold mt-1 font-['Manrope']">{user?.name}</h1>
          <p className="mt-2 opacity-80 max-w-xl">Here&apos;s what&apos;s happening at Vibe6 Digital today</p>
        </div>
        <div className="absolute right-0 top-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/4" />
        <div className="absolute right-20 bottom-0 w-32 h-32 bg-white/10 rounded-full translate-y-1/2" />
      </div>

      <Tabs value={activeTab} onValueChange={(t) => { setActiveTab(t); if (t === 'analytics') loadAnalytics(); }}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="analytics-tab">Analytics</TabsTrigger>
          </TabsList>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => downloadReport('weekly')} disabled={downloading} data-testid="download-weekly-btn">
              {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />} Weekly PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadReport('monthly')} disabled={downloading} data-testid="download-monthly-btn">
              {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />} Monthly PDF
            </Button>
          </div>
        </div>

        <TabsContent value="overview" className="space-y-6 mt-4">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard title="Active Employees" value={stats?.employees || 0} icon={Users} description="Total active" href="/employees" />
            <StatCard title="Pending Leaves" value={stats?.pending_leaves || 0} icon={CalendarDays} description={canApprove ? 'Needs your review' : 'Awaiting approval'} href="/leaves" />
            <StatCard title="Pending Expenses" value={stats?.pending_expenses || 0} icon={Receipt} description={stats?.total_expenses_pending ? formatCurrency(stats.total_expenses_pending) : ''} href="/expenses" />
            <StatCard title="Holidays This Year" value={stats?.total_holidays || 0} icon={PartyPopper} description={stats?.upcoming_holidays ? `${stats.upcoming_holidays} upcoming` : 'No upcoming'} href="/holidays" />
          </div>

          {/* Next Holiday */}
          {stats?.next_holiday && (
            <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 cursor-pointer hover:shadow-md transition-all" onClick={() => navigate('/holidays')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-amber-100"><CalendarCheck className="w-6 h-6 text-amber-600" /></div>
                    <div>
                      <p className="text-sm text-amber-700 font-medium">Next Holiday</p>
                      <p className="text-lg font-bold text-amber-900">{stats.next_holiday.name}</p>
                      <p className="text-sm text-amber-600">{formatDate(stats.next_holiday.date)} &bull; {stats.next_holiday.type}</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-amber-400" />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Second row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard title="Available Assets" value={`${stats?.available_assets || 0}/${stats?.total_assets || 0}`} icon={Package} description="Ready to assign" href="/assets" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* CRM Stats */}
            <Card className="lg:col-span-1 cursor-pointer hover:shadow-md hover:border-primary/50 transition-all" onClick={() => navigate('/crm')}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between text-lg">
                  <span className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-primary" />CRM Overview</span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-3"><Clock className="w-5 h-5 text-yellow-600" /><span className="text-sm font-medium">Active Leads</span></div>
                  <span className="text-2xl font-bold">{stats?.active_leads || 0}</span>
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg bg-green-50">
                  <div className="flex items-center gap-3"><CheckCircle className="w-5 h-5 text-green-600" /><span className="text-sm font-medium">Won Deals</span></div>
                  <span className="text-2xl font-bold text-green-600">{stats?.won_leads || 0}</span>
                </div>
              </CardContent>
            </Card>

            {/* Document Expiry */}
            {isHRAdmin && expiryData && (
              <Card className="lg:col-span-1 cursor-pointer hover:shadow-md hover:border-amber-400/50 transition-all border-amber-200" onClick={() => navigate('/settings')}>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center justify-between text-lg">
                    <span className="flex items-center gap-2"><FileWarning className="w-5 h-5 text-amber-600" />Document Expiry</span>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </CardTitle>
                  <CardDescription>Employee documents status</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {(expiryData.total_expired > 0 || expiryData.urgent_7_days > 0) ? (
                    <>
                      {expiryData.total_expired > 0 && (
                        <div className="flex items-center justify-between p-3 rounded-lg bg-red-50 border border-red-200">
                          <div className="flex items-center gap-3"><AlertTriangle className="w-5 h-5 text-red-600" /><span className="text-sm font-medium text-red-800">Expired</span></div>
                          <span className="text-xl font-bold text-red-600">{expiryData.total_expired}</span>
                        </div>
                      )}
                      {expiryData.urgent_7_days > 0 && (
                        <div className="flex items-center justify-between p-3 rounded-lg bg-orange-50 border border-orange-200">
                          <div className="flex items-center gap-3"><Clock className="w-5 h-5 text-orange-600" /><span className="text-sm font-medium text-orange-800">Expiring in 7 days</span></div>
                          <span className="text-xl font-bold text-orange-600">{expiryData.urgent_7_days}</span>
                        </div>
                      )}
                      {expiryData.warning_14_days > 0 && (
                        <div className="flex items-center justify-between p-3 rounded-lg bg-yellow-50 border border-yellow-200">
                          <div className="flex items-center gap-3"><Clock className="w-5 h-5 text-yellow-600" /><span className="text-sm font-medium text-yellow-800">Expiring in 14 days</span></div>
                          <span className="text-xl font-bold text-yellow-600">{expiryData.warning_14_days}</span>
                        </div>
                      )}
                      {expiryData.critical_count > 0 && <Badge variant="destructive" className="w-full justify-center py-1">{expiryData.critical_count} Critical Document{expiryData.critical_count > 1 ? 's' : ''} Need Attention</Badge>}
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
                      <p className="text-sm font-medium text-green-600">All Clear!</p>
                      <p className="text-xs text-muted-foreground">No documents expiring soon</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* DevOps Work Items */}
            <Card className={`${isHRAdmin && expiryData ? 'lg:col-span-1' : 'lg:col-span-2'}`}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg"><TrendingUp className="w-5 h-5 text-primary" />Recent Work Items</CardTitle>
                <CardDescription>From Azure DevOps</CardDescription>
              </CardHeader>
              <CardContent>
                {workItems.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground"><p>No work items to display</p><p className="text-sm mt-1">Connect Azure DevOps to see your tasks</p></div>
                ) : (
                  <div className="space-y-3">
                    {workItems.map((item) => (
                      <div key={item.id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{item.title}</p>
                          <p className="text-xs text-muted-foreground">#{item.id} &middot; {item.work_item_type}</p>
                        </div>
                        <Badge variant="outline" className="ml-2">{item.state}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6"><AttendanceStatsWidget /></div>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6 mt-4">
          {analyticsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-80" />)}
            </div>
          ) : analytics ? (
            <>
              {/* Revenue summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card><CardContent className="pt-4 text-center"><p className="text-sm text-muted-foreground">Total Invoiced</p><p className="text-2xl font-bold text-primary">Rs. {formatINR(analytics.revenue_summary?.total)}</p></CardContent></Card>
                <Card><CardContent className="pt-4 text-center"><p className="text-sm text-muted-foreground">Revenue Paid</p><p className="text-2xl font-bold text-green-600">Rs. {formatINR(analytics.revenue_summary?.paid)}</p></CardContent></Card>
                <Card><CardContent className="pt-4 text-center"><p className="text-sm text-muted-foreground">Active CRM Leads</p><p className="text-2xl font-bold">{analytics.crm_pipeline?.reduce((s, d) => s + d.count, 0)}</p></CardContent></Card>
                <Card><CardContent className="pt-4 text-center"><p className="text-sm text-muted-foreground">Total Deal Value</p><p className="text-2xl font-bold text-violet-600">Rs. {formatINR(analytics.crm_pipeline?.reduce((s, d) => s + d.deal_value, 0))}</p></CardContent></Card>
              </div>

              {/* Charts Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <CRMPipelineChart data={analytics.crm_pipeline} />
                <TimesheetTrendChart data={analytics.timesheet_trend} />
                <ExpenseBreakdownChart data={analytics.expense_breakdown} />
                <DepartmentChart data={analytics.department_distribution} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <HeadcountChart data={analytics.headcount_trend} />
                {/* Leave breakdown */}
                <Card data-testid="leave-stats-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Leave Summary</CardTitle>
                    <CardDescription>By type and status</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {analytics.leave_stats?.map((ls) => (
                        <div key={ls.type} className="flex items-center justify-between p-3 rounded-lg border">
                          <span className="font-medium text-sm">{ls.type}</span>
                          <div className="flex gap-3 text-xs">
                            <span className="text-green-600">{ls.approved} approved</span>
                            <span className="text-yellow-600">{ls.pending} pending</span>
                            <span className="text-red-600">{ls.rejected} rejected</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          ) : (
            <div className="text-center py-20 text-muted-foreground"><BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" /><p>Click the Analytics tab to load charts</p></div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function formatINR(val) {
  if (!val) return '0';
  if (val >= 10000000) return `${(val / 10000000).toFixed(1)}Cr`;
  if (val >= 100000) return `${(val / 100000).toFixed(1)}L`;
  if (val >= 1000) return `${(val / 1000).toFixed(0)}K`;
  return val.toLocaleString('en-IN');
}
